import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Bell, 
  Plus, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Settings,
  MapPin,
  Zap,
  Battery,
  Timer
} from 'lucide-react';
import { GpsLicenseGate } from '@/components/gps/GpsLicenseGate';
import { AlertRulesManager } from '@/components/gps/AlertRulesManager';
import { AlertEventsManager } from '@/components/gps/AlertEventsManager';

// Mock data - replace with actual API calls
const mockAlertRules = [
  {
    id: '1',
    name: 'Excesso de Velocidade - Marginal Pinheiros',
    type: 'overspeed',
    threshold: 80,
    active: true,
    geofence_name: 'Marginal Pinheiros',
    notify_channels: { email: ['ops@empresa.com'], webhook: ['https://webhook.site/...'] },
    created_at: '2025-09-20T10:00:00Z'
  },
  {
    id: '2',
    name: 'Entrada em Área Restrita',
    type: 'enter',
    threshold: null,
    active: true,
    geofence_name: 'Centro de São Paulo',
    notify_channels: { email: ['seguranca@empresa.com'] },
    created_at: '2025-09-21T14:30:00Z'
  },
  {
    id: '3',
    name: 'Bateria Baixa',
    type: 'battery_low',
    threshold: 20,
    active: true,
    geofence_name: null,
    notify_channels: { email: ['ti@empresa.com'] },
    created_at: '2025-09-22T08:15:00Z'
  }
];

const mockAlertEvents = [
  {
    id: '1',
    rule_id: '1',
    rule_name: 'Excesso de Velocidade - Marginal Pinheiros',
    device_name: 'Caminhão 001',
    vehicle_plate: 'ABC-1234',
    happened_at: '2025-09-28T09:30:00Z',
    payload: { speed_kmh: 95, limit: 80, location: 'Marginal Pinheiros, KM 15' },
    ack_by: null,
    ack_at: null,
    priority: 'high' as const
  },
  {
    id: '2',
    rule_id: '2',
    rule_name: 'Entrada em Área Restrita',
    device_name: 'Van 003',
    vehicle_plate: 'DEF-5678',
    happened_at: '2025-09-28T08:45:00Z',
    payload: { geofence: 'Centro de São Paulo', action: 'entered' },
    ack_by: 'João Silva',
    ack_at: '2025-09-28T09:00:00Z',
    priority: 'medium' as const
  },
  {
    id: '3',
    rule_id: '3',
    rule_name: 'Bateria Baixa',
    device_name: 'Tracker GPS-007',
    vehicle_plate: 'GHI-9012',
    happened_at: '2025-09-28T07:20:00Z',
    payload: { battery_level: 15, threshold: 20 },
    ack_by: null,
    ack_at: null,
    priority: 'low' as const
  }
];

export default function GpsAlerts() {
  const [activeTab, setActiveTab] = useState('events');

  const getAlertTypeIcon = (type: string) => {
    switch (type) {
      case 'overspeed': return <Zap className="h-4 w-4" />;
      case 'enter':
      case 'exit': return <MapPin className="h-4 w-4" />;
      case 'battery_low': return <Battery className="h-4 w-4" />;
      case 'idle': return <Timer className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getAlertTypeLabel = (type: string) => {
    switch (type) {
      case 'overspeed': return 'Excesso de Velocidade';
      case 'enter': return 'Entrada em Área';
      case 'exit': return 'Saída de Área';
      case 'battery_low': return 'Bateria Baixa';
      case 'idle': return 'Tempo Ocioso';
      case 'power_loss': return 'Perda de Energia';
      default: return 'Desconhecido';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-destructive';
      case 'medium': return 'bg-warning';
      case 'low': return 'bg-muted-foreground';
      default: return 'bg-muted-foreground';
    }
  };

  // Statistics
  const stats = {
    totalRules: mockAlertRules.length,
    activeRules: mockAlertRules.filter(r => r.active).length,
    totalEvents: mockAlertEvents.length,
    pendingEvents: mockAlertEvents.filter(e => !e.ack_at).length,
    highPriorityEvents: mockAlertEvents.filter(e => e.priority === 'high' && !e.ack_at).length
  };

  return (
    <GpsLicenseGate>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Bell className="h-8 w-8 text-primary" />
              Alertas GPS
            </h1>
            <p className="text-muted-foreground">
              Configure regras e monitore eventos da frota
            </p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Regras Ativas</p>
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-success" />
                  <p className="text-2xl font-bold">{stats.activeRules}</p>
                  <span className="text-xs text-muted-foreground">/{stats.totalRules}</span>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Eventos Pendentes</p>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-warning" />
                  <p className="text-2xl font-bold">{stats.pendingEvents}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Alta Prioridade</p>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  <p className="text-2xl font-bold">{stats.highPriorityEvents}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Total Eventos</p>
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-primary" />
                  <p className="text-2xl font-bold">{stats.totalEvents}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Resolvidos</p>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-success" />
                  <p className="text-2xl font-bold">{stats.totalEvents - stats.pendingEvents}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="events" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Eventos
            </TabsTrigger>
            <TabsTrigger value="rules" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Regras
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="space-y-4">
            <AlertEventsManager 
              events={mockAlertEvents}
              onAcknowledge={(eventId, userId) => {
                console.log('Acknowledge event:', eventId, 'by:', userId);
              }}
            />
          </TabsContent>

          <TabsContent value="rules" className="space-y-4">
            <AlertRulesManager 
              rules={mockAlertRules}
              onCreateRule={(rule) => {
                console.log('Create rule:', rule);
              }}
              onUpdateRule={(ruleId, rule) => {
                console.log('Update rule:', ruleId, rule);
              }}
              onDeleteRule={(ruleId) => {
                console.log('Delete rule:', ruleId);
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
    </GpsLicenseGate>
  );
}